import type { PublicComplaint } from '@residenone/shared';
import { listAdminComplaints } from './complaints-api';

export interface ComplaintsSummary {
  created: number;
  done: number;
  inProgress: number;
  /** Filed but nobody has picked it up yet (status NEW). */
  waiting: number;
  doneRate: number;
}

/** Cancelled complaints were withdrawn by the resident, so they count as neither filed nor done. */
export function summarizeComplaints(items: PublicComplaint[]): ComplaintsSummary {
  const live = items.filter((i) => i.status !== 'CANCELLED');
  const done = live.filter((i) => i.status === 'RESOLVED').length;
  const inProgress = live.filter((i) => i.status === 'IN_PROGRESS').length;
  const waiting = live.filter((i) => i.status === 'NEW').length;
  return { created: live.length, done, inProgress, waiting, doneRate: live.length > 0 ? done / live.length : 0 };
}

/**
 * Complaints filed since the start of the month three months back, newest first.
 * Wide enough to cover everything resolved last month, which drives the
 * response-time comparison, without reading the whole history.
 */
export async function fetchRecentComplaints(today: Date): Promise<PublicComplaint[]> {
  const since = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 3, 1)).toISOString();
  const all: PublicComplaint[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < 20; page++) {
    const res = await listAdminComplaints({ limit: 100, ...(cursor ? { cursor } : {}) });
    all.push(...res.items);
    const oldest = res.items[res.items.length - 1];
    if (!res.nextCursor || (oldest && oldest.created_at < since)) break;
    cursor = res.nextCursor;
  }
  return all.filter((c) => c.created_at >= since);
}

/** Complaints filed this calendar month, from an already fetched list. */
export function complaintsThisMonth(items: PublicComplaint[], today: Date): ComplaintsSummary {
  const month = today.toISOString().slice(0, 7);
  return summarizeComplaints(items.filter((c) => c.created_at.slice(0, 7) === month));
}
